import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { errorMessage } from '../../lib/errors'
import type { TaskFilters as Filters } from '../../types/domain'
import { listCatalog } from '../catalogs/catalog.repository'
import { TaskFilters } from './TaskFilters'
import { TaskList } from './TaskList'
import { useTasks } from './task.queries'

const initialFilters: Filters = {
  clientId: '',
  assigneeId: '',
  departmentId: '',
  priority: '',
  source: '',
  status: '',
  archived: false,
  todayOnly: false,
}

export function DashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [filters, setFilters] = useState(() => ({
    ...initialFilters,
    archived: searchParams.get('arquivadas') === '1',
    todayOnly: searchParams.get('hoje') === '1',
  }))
  const tasks = useTasks(filters)
  const clients = useQuery({ queryKey: ['catalog', 'clients'], queryFn: () => listCatalog('clients') })
  const assignees = useQuery({ queryKey: ['catalog', 'assignees'], queryFn: () => listCatalog('assignees') })
  const departments = useQuery({ queryKey: ['catalog', 'departments'], queryFn: () => listCatalog('departments') })

  const groups = useMemo(() => {
    const data = tasks.data ?? []
    return {
      overdue: data.filter((task) => task.is_overdue),
      dueToday: data.filter((task) => task.is_due_today && !task.is_overdue),
      dueSoon: data.filter((task) => task.is_due_soon && !task.is_overdue),
      remaining: data.filter((task) => !task.is_overdue && !task.is_due_soon),
    }
  }, [tasks.data])

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm font-bold uppercase tracking-wider text-blue-700">Visão do dia</p>
          <h1 className="text-3xl font-extrabold text-slate-950">{filters.archived ? 'Tarefas arquivadas' : filters.todayOnly ? 'Tarefas de hoje' : 'Painel de tarefas'}</h1>
          <p className="mt-1 text-slate-600">{filters.todayOnly ? 'Pendências vencidas e tarefas que ainda vencem hoje, no horário de Brasília.' : 'Priorize o que venceu e o que se aproxima do prazo.'}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            className="btn-secondary"
            type="button"
            onClick={() => {
              const archived = !filters.archived
              setFilters((current) => ({ ...current, archived, todayOnly: false }))
              setSearchParams(archived ? { arquivadas: '1' } : {}, { replace: true })
            }}
          >
            {filters.archived ? 'Ver tarefas ativas' : 'Ver arquivadas'}
          </button>
          {!filters.archived ? (
            <button
              className={filters.todayOnly ? 'btn-primary' : 'btn-secondary'}
              type="button"
              onClick={() => {
                const todayOnly = !filters.todayOnly
                setFilters((current) => ({ ...current, archived: false, todayOnly }))
                setSearchParams(todayOnly ? { hoje: '1' } : {}, { replace: true })
              }}
            >
              {filters.todayOnly ? 'Ver painel completo' : 'Tarefas de hoje'}
            </button>
          ) : null}
          <Link className="btn-primary text-center" to="/tarefas/nova">+ Nova tarefa</Link>
        </div>
      </header>

      {!filters.archived ? (
        <div className="mb-5 grid gap-3 sm:grid-cols-3">
          <div className="card border-l-4 border-l-red-500 p-4"><span className="text-sm font-bold text-slate-600">Atrasadas</span><strong className="mt-1 block text-3xl text-red-700">{groups.overdue.length}</strong></div>
          <div className="card border-l-4 border-l-amber-500 p-4"><span className="text-sm font-bold text-slate-600">{filters.todayOnly ? 'Vencem hoje' : 'Próximos 3 dias'}</span><strong className="mt-1 block text-3xl text-amber-700">{filters.todayOnly ? groups.dueToday.length : groups.dueSoon.length}</strong></div>
          <div className="card border-l-4 border-l-blue-500 p-4"><span className="text-sm font-bold text-slate-600">Total no filtro</span><strong className="mt-1 block text-3xl text-blue-700">{tasks.data?.length ?? 0}</strong></div>
        </div>
      ) : null}

      <TaskFilters value={filters} clients={clients.data ?? []} assignees={assignees.data ?? []} departments={departments.data ?? []} onChange={setFilters} />

      {tasks.isLoading ? <p className="mt-8 text-slate-600">Carregando tarefas…</p> : null}
      {tasks.isError ? <p className="notice-error mt-5" role="alert">{errorMessage(tasks.error)}</p> : null}
      {!tasks.isLoading && !tasks.isError ? (
        <div className="mt-7 grid gap-8">
          {filters.archived ? (
            <TaskList title="Arquivadas" tasks={tasks.data ?? []} emptyMessage="Nenhuma tarefa arquivada com estes filtros." />
          ) : filters.todayOnly ? (
            <>
              <TaskList title="Atrasadas" tasks={groups.overdue} emptyMessage="Nenhuma tarefa atrasada." />
              <TaskList title="Vencem hoje" tasks={groups.dueToday} emptyMessage="Nenhuma outra tarefa vence hoje." />
            </>
          ) : (
            <>
              <TaskList title="Atrasadas" tasks={groups.overdue} emptyMessage="Nenhuma tarefa atrasada." />
              <TaskList title="Prazo próximo" tasks={groups.dueSoon} emptyMessage="Nenhuma tarefa vence nos próximos 3 dias." />
              <TaskList title="Demais tarefas" tasks={groups.remaining} emptyMessage="Nenhuma outra tarefa com estes filtros." />
            </>
          )}
        </div>
      ) : null}
    </div>
  )
}
